import React, { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { api } from "../lib/api";

/**
 * GCRAdBanner — renders ads for a specific position slot on the GCR Theaters page.
 * Position values: "top" | "middle" | "bottom".
 * - Public endpoint: GET /api/gcr/ads?position=...
 * - Click handler: POST /api/gcr/ads/{ad_id}/click (best-effort, fire-and-forget)
 * - Falls back silently when no active ads exist for the position.
 */
export default function GCRAdBanner({ position = "top" }) {
  const [ads, setAds] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.get("/gcr/ads", { params: { position } })
      .then(({ data }) => { if (!cancelled) setAds(data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [position]);

  // Auto-rotate when there are multiple ads in the same slot
  useEffect(() => {
    if (ads.length <= 1) return;
    const t = setInterval(() => setActive((i) => (i + 1) % ads.length), 6000);
    return () => clearInterval(t);
  }, [ads.length]);

  if (ads.length === 0) return null;
  const ad = ads[active];

  const handleClick = (e) => {
    // Fire-and-forget click counter
    api.post(`/gcr/ads/${ad.ad_id}/click`).catch(() => {});
    if (!ad.click_url) {
      e.preventDefault();
      return;
    }
    // Internal links open in-app; external open in a new tab
    if (ad.click_url.startsWith("/")) {
      e.preventDefault();
      window.location.assign(ad.click_url);
    }
    // else default browser behaviour (external link via target=_blank)
  };

  const isExternal = ad.click_url && !ad.click_url.startsWith("/");

  return (
    <section data-testid={`gcr-ad-${position}`} className="mb-6 relative group">
      <a
        href={ad.click_url || "#"}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
        onClick={handleClick}
        data-testid={`gcr-ad-link-${ad.ad_id}`}
        className="block relative rounded-lg overflow-hidden border border-white/5 hover:border-[#E50914]/40 transition-all"
      >
        <div className="relative w-full" style={{ aspectRatio: "8 / 1", maxHeight: "180px", minHeight: "100px" }}>
          <img
            src={ad.image_url}
            alt={ad.title}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          {ad.caption && (
            <>
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-transparent" />
              <div className="absolute inset-0 flex items-center px-6 lg:px-10">
                <div className="max-w-xl">
                  <p className="text-white font-bold text-base sm:text-lg lg:text-xl drop-shadow-lg line-clamp-2">
                    {ad.caption}
                  </p>
                  {ad.click_url && (
                    <p className="text-[10px] uppercase tracking-widest text-white/70 mt-1 flex items-center gap-1">
                      Tap to open {isExternal && <ExternalLink className="w-3 h-3" />}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
          <span className="absolute top-2 right-2 text-[9px] uppercase tracking-widest bg-black/70 text-gray-300 px-2 py-0.5 rounded">
            Sponsored
          </span>
        </div>
      </a>

      {/* Dots for multi-ad rotation */}
      {ads.length > 1 && (
        <div className="absolute bottom-2 right-3 flex items-center gap-1.5">
          {ads.map((_, i) => (
            <button
              key={i}
              data-testid={`gcr-ad-dot-${i}`}
              onClick={() => setActive(i)}
              className={`w-1.5 h-1.5 rounded-full transition-all ${i === active ? "bg-white w-4" : "bg-white/40 hover:bg-white/70"}`}
              aria-label={`Show ad ${i + 1} of ${ads.length}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
